import type { Folder, Group, Permission, Rights, Share } from '../../types.ts'
import { getPermissionEntry, getSharePermissionEntry } from './matrix-helpers.ts'

/**
 * Ziel eines Fix-Vorschlags: entweder ein konkreter Ordner oder der
 * Share-Root. Genau EINES der beiden Felder ist gesetzt — analog zu
 * `Permission` selbst.
 */
export type SuggestionTarget =
  | { kind: 'folder'; folderId: string }
  | { kind: 'share'; shareId: string }

export interface PermissionSuggestion {
  /** Stabiler Dedup-Key, zugleich React-key. */
  id: string
  target: SuggestionTarget
  /** Anzeigepfad, z.B. `GEW\Share-Root` oder `GEW\Abfallberatung`. */
  targetLabel: string
  /** Kurz-Kategorie fuers UI: "Share-Root" oder "Ordner". */
  targetKind: 'Share-Root' | 'Ordner'
  groupId: string
  groupLabel: string
  /** Aktuell am Ziel direkt gesetztes Recht (null = erbt / kein ACE). */
  currentRights: Rights | null
  /** Ursache am Ziel in Prosa, z.B. "DENY (kein Zugriff)". */
  currentIssue: string
  /** Minimales Traversal-Recht — `ListFolder` reicht, damit Windows die Kette durchlaesst. */
  suggestedRights: Rights
  /** IDs der Zielordner, deren Zugriff durch diesen Knoten mit-blockiert wird. */
  affectedFolderIds: string[]
}

/**
 * Baut den Ordner-Pfad als Backslash-getrennter String (ohne Share-Prefix).
 */
function folderPath(folderId: string, byId: Map<string, Folder>): string {
  const names: string[] = []
  let cur: Folder | undefined = byId.get(folderId)
  while (cur) {
    names.unshift(cur.name)
    cur = cur.parent ? byId.get(cur.parent) : undefined
  }
  return names.join('\\')
}

function describeRights(r: Rights | null): string {
  if (!r) return 'keine Berechtigung'
  if (r === 'None') return 'DENY (kein Zugriff)'
  if (r === 'Revoke') return 'Entzogen (kein ACL-Eintrag)'
  return r
}

/**
 * „Blockierend" heisst: es gibt keinen direkten Berechtigungseintrag ODER der
 * vorhandene ist DENY bzw. Revoke. Vererbung zaehlt hier NICHT — mit dem
 * `noInherit`-Scope (den die Vorschlaege setzen) muss jeder Knoten im Pfad ein
 * eigenes Traversal-Recht haben, damit Windows die Kette durchlaesst.
 */
function isBlocking(entry: Permission | null): boolean {
  if (!entry) return true
  return entry.rights === 'None' || entry.rights === 'Revoke'
}

/**
 * Aus den Warnungen der Plausibilitaetspruefung minimale Fix-Vorschlaege ableiten.
 *
 * **Minimum-Privilege-Prinzip:** Damit eine Gruppe Zugriff auf einen
 * Zielordner hat, benoetigt sie auf JEDEM Zwischenordner bis zum Share-Root
 * ein Traversal-Recht — aber ohne Vererbung, damit Geschwister-Ordner nichts
 * „abbekommen". Der Algorithmus laeuft die komplette Kette vom Zielordner hoch
 * und sammelt jeden Knoten, der aktuell keinen eigenen, durchlaessigen
 * Berechtigungseintrag hat. Jeder solche Knoten wird mit `ListFolder`
 * (Scope „Nur dieser Ordner", d.h. `noInherit: true`) ausgestattet.
 *
 * **Deduplikation:** Wenn fuenf Zielordner im selben Share liegen und alle am
 * Share-Root UND an `Abteilung 3` scheitern, entstehen nicht 10 Eintraege,
 * sondern 2 — jeder mit Impact-Zahl 5.
 */
export function buildPermissionSuggestions(params: {
  issues: Map<string, Map<string, { ancestorName: string; issue: string }>>
  allFolders: Folder[]
  allPermissions: Permission[]
  shares: Share[]
  groups: Group[]
  groupLabel: (g: Group) => string
}): PermissionSuggestion[] {
  const { issues, allFolders, allPermissions, shares, groups, groupLabel } = params
  const folderById = new Map(allFolders.map((f) => [f.id, f] as const))
  const shareById = new Map(shares.map((s) => [s.id, s] as const))
  const groupById = new Map(groups.map((g) => [g.id, g] as const))

  const byKey = new Map<string, PermissionSuggestion>()

  function upsert(
    target: SuggestionTarget,
    groupId: string,
    existingEntry: Permission | null,
    affectedFolderId: string,
  ) {
    const key =
      target.kind === 'folder'
        ? `folder:${target.folderId}:${groupId}`
        : `share:${target.shareId}:${groupId}`

    let s = byKey.get(key)
    if (!s) {
      const group = groupById.get(groupId)
      const label = group ? groupLabel(group) : groupId

      let targetLabel: string
      let targetKind: 'Share-Root' | 'Ordner'
      if (target.kind === 'folder') {
        targetLabel = folderPath(target.folderId, folderById)
        targetKind = 'Ordner'
      } else {
        const sh = shareById.get(target.shareId)
        targetLabel = sh ? `\\${sh.name}${sh.isHidden ? '$' : ''}` : '(Share)'
        targetKind = 'Share-Root'
      }

      const directRights = existingEntry?.rights ?? null
      s = {
        id: key,
        target,
        targetLabel,
        targetKind,
        groupId,
        groupLabel: label,
        currentRights: directRights,
        currentIssue: describeRights(directRights),
        suggestedRights: 'ListFolder',
        affectedFolderIds: [],
      }
      byKey.set(key, s)
    }
    if (!s.affectedFolderIds.includes(affectedFolderId)) {
      s.affectedFolderIds.push(affectedFolderId)
    }
  }

  for (const [folderId, perGroup] of issues) {
    for (const [groupId] of perGroup) {
      const folder = folderById.get(folderId)
      if (!folder) continue

      // Alle Vorfahren ABLAUFEN — jeder Knoten ohne eigenen durchlaessigen
      // Eintrag wird als Fix-Kandidat aufgenommen. Der Zielordner selbst
      // nicht — der hat ja per Voraussetzung bereits effektive Rechte.
      let current: Folder = folder
      while (current.parent) {
        const parent = folderById.get(current.parent)
        if (!parent) break
        const entry = getPermissionEntry(allPermissions, parent.id, groupId)
        if (isBlocking(entry)) {
          upsert({ kind: 'folder', folderId: parent.id }, groupId, entry, folderId)
        }
        current = parent
      }

      // Share-Root abschliessen.
      const shareEntry = getSharePermissionEntry(allPermissions, folder.shareId, groupId)
      if (isBlocking(shareEntry)) {
        upsert({ kind: 'share', shareId: folder.shareId }, groupId, shareEntry, folderId)
      }
    }
  }

  return [...byKey.values()].sort((a, b) => {
    // Share-Root zuerst, dann nach Impact, dann alphabetisch.
    if (a.targetKind !== b.targetKind) {
      return a.targetKind === 'Share-Root' ? -1 : 1
    }
    const byImpact = b.affectedFolderIds.length - a.affectedFolderIds.length
    if (byImpact !== 0) return byImpact
    return a.targetLabel.localeCompare(b.targetLabel, 'de', { sensitivity: 'base' })
  })
}

/**
 * Wendet Vorschlaege auf ein Permission-Array an: pro (target, group) wird
 * der bestehende Eintrag entfernt und durch `ListFolder` mit
 * `noInherit: true` ersetzt.
 *
 * **Warum `noInherit`?** Ohne diesen Scope wuerde ein Traversal-Recht am
 * Share-Root per NTFS-Default auf alle Unterordner vererbt und die gesamte
 * Struktur sichtbar machen. Mit `noInherit` wirkt das Recht nur auf den
 * Knoten selbst — Geschwister-Ordner bleiben unberuehrt.
 */
export function applySuggestions(
  permissions: Permission[],
  suggestions: PermissionSuggestion[],
): Permission[] {
  let out = [...permissions]
  for (const s of suggestions) {
    out = out.filter((p) => {
      if (p.groupId !== s.groupId) return true
      if (s.target.kind === 'folder') return p.folderId !== s.target.folderId
      return !(!p.folderId && p.shareId === s.target.shareId)
    })
    const perm: Permission =
      s.target.kind === 'folder'
        ? {
            folderId: s.target.folderId,
            groupId: s.groupId,
            rights: s.suggestedRights,
            noInherit: true,
          }
        : {
            shareId: s.target.shareId,
            groupId: s.groupId,
            rights: s.suggestedRights,
            noInherit: true,
          }
    out.push(perm)
  }
  return out
}
